import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  AutopilotCloseFromPauseRequest,
  AutopilotCycleSummary,
  CloseFromPauseRefusal,
} from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { formatMoney, formatSignedPercent } from "../lib/format";

type ExitNow = AutopilotCycleSummary["exitNow"];

/**
 * "as of HH:MM" (design 34/36's balance-popover convention). `BalanceSummary.tsx`'s own
 * `clockLabel` is private to that file and out of this phase's file scope to export, so this
 * mirrors it locally rather than adding a shared helper for one more caller.
 */
function clockLabel(at: string): string {
  const time = new Date(at);
  if (!Number.isFinite(time.getTime())) return "an unknown time";
  return `${String(time.getHours()).padStart(2, "0")}:${String(time.getMinutes()).padStart(2, "0")}`;
}

const CLOSE_REFUSAL_TEXT: Partial<Record<CloseFromPauseRefusal, string>> = {
  position_changed: "The position changed — nothing was closed.",
  executor_not_ready: "The vault is locked — unlock it first.",
  not_held: "There is no open position to close.",
  previous_close_cancelled:
    "An earlier close of this position was cancelled — close it from the position card.",
};
const GENERIC_CLOSE_REFUSAL_TEXT = "The position cannot be closed from pause now.";

/**
 * `apps/web/src/api/client.ts` only forwards `code`/`message`/`operationFailure` onto `ApiError`
 * (out of this phase's file scope to extend), so the `refusal` field the server sends alongside
 * `AUTOPILOT_CLOSE_REFUSED` (`apps/server/src/autopilot/commands.ts` `CloseFromPauseRefused`) never
 * reaches here directly. Its fixed message always embeds the refusal id verbatim
 * ("...(<refusal>)"), and refusal ids are a closed, known vocabulary, so matching the message
 * against that vocabulary recovers it without depending on the sentence around it.
 */
function closeErrorText(reason: unknown): string {
  if (reason instanceof ApiError) {
    if (reason.code !== "AUTOPILOT_CLOSE_REFUSED") return reason.message;
    const code = (Object.keys(CLOSE_REFUSAL_TEXT) as CloseFromPauseRefusal[]).find((candidate) =>
      new RegExp(`\\b${candidate}\\b`).test(reason.message),
    );
    return (code ? CLOSE_REFUSAL_TEXT[code] : undefined) ?? GENERIC_CLOSE_REFUSAL_TEXT;
  }
  return reason instanceof Error ? reason.message : "Close was not accepted";
}

/** Close from pause (design 47/49's bot confirmation, mirrored here): same rules as `AutopilotPauseDialog`. */
export function AutopilotCloseDialog({
  pair,
  positionId,
  exitNow,
  onClose,
  onClosed,
}: {
  pair: string;
  /** The position the operator confirmed; the server refuses `position_changed` for any other. */
  positionId: string;
  exitNow: ExitNow;
  onClose: () => void;
  onClosed: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    back.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api("/autopilot/close", {
        method: "POST",
        body: { idempotencyKey, positionId } satisfies AutopilotCloseFromPauseRequest,
        sensitive: true,
      });
      onClosed();
    } catch (reason) {
      setError(closeErrorText(reason));
    } finally {
      setPending(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      className="position-action-modal autopilot-dialog autopilot-close-dialog"
      aria-labelledby="autopilot-close-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header className="modal-header">
        <div>
          <h2 id="autopilot-close-title">Close position?</h2>
          <small>{pair}</small>
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <form onSubmit={(e) => void submit(e)}>
        <div className="position-action-body autopilot-form">
          <dl className="confirmation-facts">
            <div>
              <dt>Back</dt>
              <dd>
                {exitNow.value === null
                  ? formatMoney(exitNow.value)
                  : `≈ ${formatMoney(exitNow.value)}${
                      exitNow.at === null ? "" : ` · as of ${clockLabel(exitNow.at)}`
                    }`}
              </dd>
            </div>
            {exitNow.resultPercent !== null && (
              <div>
                <dt>Result</dt>
                <dd>{formatSignedPercent(exitNow.resultPercent)}</dd>
              </div>
            )}
          </dl>
          <p className="confirmation-note">
            This cannot be undone. The autopilot stays paused afterwards.
          </p>
          {error && (
            <p className="inline-alert warning" role="alert">
              {error}
            </p>
          )}
        </div>
        <footer>
          <button ref={back} type="button" className="button" onClick={onClose}>
            Back
          </button>
          <button className="button danger" disabled={pending}>
            {pending ? "Closing…" : "Close position"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
